import React, {useEffect, useState} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { Breadcrumb, BackdropCircular } from 'components';
import {getCustomerSubscriptionsListApi} from 'api';
import useLocalStorageTableCells from 'hooks/useLocalStorageTableCells';
import { parseSearchParams } from 'helpers/customers';
import httpBuildQuery from 'http-build-query';
import SubscriptionsTableToolbar from './SubscriptionsTableToolbar';
import { useStyles } from './styles';
import SubscriptionsFilterDrawer from './SubscriptionsFilterDrawer';
import SubscriptionsRowCollapsible from './SubscriptionsRowCollapsible';


const defaultTableCells = [
  {
    label: 'Id',
    sortField: 'increment_id',
    display: true,
  },
  {
    label: 'Description',
    sortField: 'description',
    display: true,
  },
  {
    label: 'Status',
    sortField: 'status',
    display: true,
  },
  {
    label: 'First Name',
    sortField: 'customer_firstname',
    display: true,
  },
  {
    label: 'Last Name',
    sortField: 'customer_lastname',
    display: true,
  },
  {
    label: 'Created',
    sortField: 'created_at',
    display: true,
  },
  {
    label: 'Last Run',
    sortField: 'last_run',
    display: true,
  },
  {
    label: 'Next Run',
    sortField: 'next_run',
    display: true,
  },
  {
    label: 'Times Billed',
    sortField: 'run_count',
    display: true,
  },
  {
    label: 'Subtotal',
    display: true,
  },
  {
    label: 'Action',
    display: true,
  }
];

const Subscriptions = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const paramsFromUrl: IParamsFromUrl = parseSearchParams(location.search);
  const { customerSubscriptionsList, isCustomerSubscriptionsLoading, customerSubscriptionsTotalCount, downloadingSubscriptionReport, isCustomerSubscriptionPriceLoading } = useSelector((state: IReduxState) => state.customer_subscriptions);
  const { loadingSARPUpdateConfig } = useSelector((state: IReduxState) => state.store_configs);
  const [sortDirection, setSortDirection] = useState(paramsFromUrl.sortDirection || 'desc');
  const [sortField, setSortField] = useState(paramsFromUrl.sortField || 'created_at');
  const [currentPage, setCurrentPage] = useState(paramsFromUrl.currentPage || 1);
  const [rowsPerPage, setRowsPerPage] = useState(paramsFromUrl.rowsPerPage || 10);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterConditions, setFilterConditions] = useState<IFilterConditions>(paramsFromUrl.filterConditions || {});
  const [searchConditions, setSearchConditions] = useState(paramsFromUrl.searchConditions || {});
  const { headTableCells, setHeadTableCells } = useLocalStorageTableCells('SubscriptionsTableCells', defaultTableCells);

  useEffect(() => {
    getCustomerSubscriptionsListApi({
      dispatch,
      sortDirection,
      sortField,
      pageSize: rowsPerPage,
      currentPage,
      filterConditions: Object.values(filterConditions)
        .filter((condition) => condition.value !== ''),
      searchConditions
    });
  }, [
    sortField,
    sortDirection,
    rowsPerPage,
    currentPage,
    filterConditions,
    searchConditions
  ]);

  useEffect(() => {
    const queryParams = {
      filterConditions,
      searchConditions,
      currentPage,
      sortDirection,
      sortField,
      rowsPerPage,
    };

    const queryString = httpBuildQuery(queryParams);
    history.replace({
      pathname: location.pathname,
      search: queryString,
    });
  }, [filterConditions, searchConditions, currentPage, sortDirection, sortField, rowsPerPage]);

  const onSearch = (searchValue, searchField) => {
    const condition = {
      field: searchField,
      value: searchValue.length ? `%${searchValue}%` : '',
      conditionType: 'like'
    };
    setSearchConditions(searchValue.length ? condition : {});
    setCurrentPage(1);
  };

  const handleRequestSort = (event, property) => {
    const isDesc = sortField === property && sortDirection === 'desc';
    setSortDirection(isDesc ? 'asc' : 'desc');
    setSortField(property);
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y pb-5">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            { name: 'Subscriptions'}
          ]}
        />
      </div>
      <div className={classes.card}>
        <div className={`p-5 ${classes.toolbarContainer}`}>
          <SubscriptionsTableToolbar
            onSearch={onSearch}
            setOpenFilter={setOpenFilter}
            filterConditions={filterConditions}
            tableCells={headTableCells}
            setHeadTableCells={setHeadTableCells}
          />
        </div>
        <div className="table-container">
          <TableContainer>
            <Table aria-label="collapsible table" className={classes.table}>
              <TableHead>
                <TableRow>
                  {headTableCells.map((cell, index) => {
                    if (!cell.display) {
                      return null;
                    }

                    return (
                      <TableCell
                        key={index}
                        className="min-w-80"
                      >
                        {cell.sortField ? (
                          <TableSortLabel
                            active={sortField === cell.sortField}
                            IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                            onClick={(event) => handleRequestSort(event, cell.sortField)}
                          >
                            {cell.label}
                          </TableSortLabel>
                        ) : (
                          cell.label
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {!customerSubscriptionsTotalCount ?
                  <TableRow>
                    <TableCell colSpan={Object.keys(headTableCells).length + 1} align="center">
                      No matching subscriptions found.
                    </TableCell>
                  </TableRow>
                  :
                  customerSubscriptionsList.map((el) => (
                    <SubscriptionsRowCollapsible
                      key={el.increment_id}
                      customerSubscriptionsData={el}
                      headTableCells={headTableCells}
                    />
                  ))
                }
              </TableBody>
            </Table>
          </TableContainer>
          {!!customerSubscriptionsTotalCount &&
            <TablePagination
              rowsPerPageOptions={[10, 20, 40, 80, 100]}
              component="div"
              className={classes.paginationContainer}
              count={customerSubscriptionsTotalCount}
              rowsPerPage={Number(rowsPerPage)}
              page={Number(currentPage) - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          }
        </div>
      </div>
      <Drawer
        anchor="right"
        open={openFilter}
        onClose={() => setOpenFilter((openFilter) => !openFilter)}
        PaperProps={{ style: { maxWidth: '400px' } }}
      >
        <SubscriptionsFilterDrawer
          setOpenFilter={setOpenFilter}
          setFilterConditions={setFilterConditions}
          filterConditions={filterConditions}
        />
      </Drawer>
      <BackdropCircular loading={isCustomerSubscriptionsLoading || downloadingSubscriptionReport || isCustomerSubscriptionPriceLoading || loadingSARPUpdateConfig} />
    </div>
  );
};

export default Subscriptions;
